/*
 * colour-reveal — dominant-colour placeholder reveal for [data-reveal-image].
 *
 * The reveal-image partial paints a per-image placeholder colour (from the
 * asset's extracted palette, falling back to its average colour, a param, then a
 * token) behind the <picture>. This module fades the image in over that colour as
 * it scrolls into view.
 *
 * Deliberately GSAP-FREE: it uses a plain IntersectionObserver + a CSS opacity
 * transition, so it runs on first paint without waiting for the deferred GSAP
 * bundle, and it only ever animates `opacity` (compositor thread). It never reads
 * layout.
 *
 * PROGRESSIVE ENHANCEMENT — the image is visible by default (CSS ships no hidden
 * state). This module OPTS IN to hiding-then-revealing only when it is safe to do
 * so, so every degraded path shows the image immediately:
 *   - JS off              → no state attribute is ever set → image visible.
 *   - No IntersectionObserver → we bail before touching the DOM → image visible.
 *   - prefers-reduced-motion  → we bail (and CSS also force-shows) → image visible.
 * It also degrades gracefully with NO palette data, so it is useful before the
 * ExtractPalettes command has ever run (the placeholder is just the token colour).
 *
 * Idempotent + Swup-safe: only elements WITHOUT a `data-reveal-state` are picked
 * up, so re-running on every `psd:page-view` enhances freshly swapped-in content
 * without re-hiding already-revealed images. Wired in resources/js/app.js.
 */

/** @type {IntersectionObserver | null} */
let observer = null;

/**
 * Mark an element revealed (CSS transitions its image opacity 0 → 1) and stop
 * observing it.
 *
 * @param {Element} el
 * @returns {void}
 */
function reveal(el) {
    el.setAttribute('data-reveal-state', 'revealed');
    if (observer) {
        observer.unobserve(el);
    }
}

/**
 * Hide-then-reveal every not-yet-initialised [data-reveal-image] in the document.
 *
 * Safe to call repeatedly: the `:not([data-reveal-state])` filter makes each
 * element initialise at most once.
 *
 * @returns {void}
 */
export function initColourReveal() {
    // Degraded paths: leave the image in its default-visible state untouched.
    if (
        !('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
        return;
    }

    if (!observer) {
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        reveal(entry.target);
                    }
                });
            },
            // Reveal a touch before the element reaches the viewport edge.
            { rootMargin: '0px 0px 10% 0px', threshold: 0 },
        );
    }

    document
        .querySelectorAll('[data-reveal-image]:not([data-reveal-state])')
        .forEach((el) => {
            el.setAttribute('data-reveal-state', 'pending');
            observer.observe(el);
        });
}
